import { AnimatePresence, motion } from "framer-motion";
import { X, Gift, Lock, ExternalLink } from "lucide-react";
import { DEFAULT_PRICING, formatPrice, hasPromo } from "@/lib/pricing";

export const RewardModal = ({ open, variant = "reward", reward, day, live = false, onClose, onPrimary, pricing }) => {
  const p = pricing || DEFAULT_PRICING;
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0 bg-[#030B08]/80 backdrop-blur-sm"
            onClick={onClose}
            data-testid="modal-backdrop"
          />
          <motion.div
            initial={{ opacity: 0, y: 32, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            data-testid={variant === "reward" ? "reward-modal" : "locked-modal"}
            className="relative w-full max-w-sm overflow-hidden rounded-2xl border border-amber-400/40 bg-gradient-to-b from-[#112A1E] to-[#0A1D15] p-7 text-center shadow-[0_0_70px_rgba(212,175,55,0.28)] sm:p-9"
          >
            <div className="grain-overlay pointer-events-none absolute inset-0 opacity-[0.05]" />
            <div className="pointer-events-none absolute -top-16 left-1/2 h-40 w-40 -translate-x-1/2 rounded-full bg-amber-400/15 blur-3xl" />

            <button
              type="button"
              data-testid="modal-close-button"
              aria-label="Chiudi"
              onClick={onClose}
              className="absolute right-3 top-3 z-10 rounded-full p-1.5 text-emerald-100/60 transition-colors hover:bg-white/5 hover:text-amber-200"
            >
              <X className="h-4 w-4" />
            </button>

            {variant === "reward" ? (
              <div className="relative space-y-4">
                {reward?.image_url && (
                  <img
                    src={reward.image_url}
                    alt={reward?.title || "Sorpresa"}
                    data-testid="reward-image"
                    className="h-36 w-full rounded-xl border border-amber-400/30 object-cover"
                  />
                )}
                <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-300">
                  <Gift className="h-3.5 w-3.5" /> {live ? "🎁 La tua sorpresa" : "🎁 Sorpresa demo"}
                </span>
                {day && (
                  <p className="text-xs uppercase tracking-[0.25em] text-emerald-100/50">
                    Casella {day}
                  </p>
                )}
                <h3 className="font-display text-3xl font-bold text-amber-100">{reward?.title}</h3>
                <p className="text-sm leading-relaxed text-emerald-100/75">{reward?.description}</p>
                {reward?.brand_name && (
                  <p data-testid="reward-brand" className="text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-300/80">
                    In collaborazione con {reward.brand_name}
                  </p>
                )}
                {reward?.link_url && (
                  <a
                    href={reward.link_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="reward-link"
                    className="inline-flex items-center gap-1.5 rounded-xl border border-amber-400/40 px-4 py-2.5 text-sm font-semibold text-amber-200 transition-colors hover:bg-amber-400/10"
                  >
                    <ExternalLink className="h-4 w-4" /> Vedi il riferimento del premio
                  </a>
                )}
                {onPrimary && (
                  <button
                    type="button"
                    data-testid="reward-modal-cta"
                    onClick={onPrimary}
                    className="w-full rounded-xl bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-600 px-6 py-3 font-bold text-[#1A1204] shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-amber-500/30"
                  >
                    {live ? "Meraviglioso!" : "Scopri tutte le sorprese"}
                  </button>
                )}
              </div>
            ) : (
              <div className="relative space-y-4">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-amber-400/40 bg-amber-400/10 text-amber-300">
                  <Lock className="h-5 w-5" />
                </span>
                <h3 className="font-display text-2xl font-bold text-amber-100">
                  {live
                    ? `La casella ${day} si apre il ${day} dicembre 🎄`
                    : "Questa sorpresa sarà disponibile nel tuo calendario 🎄"}
                </h3>
                <p className="text-sm leading-relaxed text-emerald-100/75">
                  {live
                    ? "Torna quel giorno: la sorpresa ti aspetterà qui."
                    : "Ogni giorno dal 1 al 24 dicembre si apre una nuova casella, solo per te."}
                </p>
                {!live && (
                  <div data-testid="locked-modal-price" className="space-y-1.5">
                    <p className="flex items-baseline justify-center gap-2">
                      {hasPromo(p) && (
                        <span data-testid="locked-modal-original-price" className="text-sm text-emerald-100/50 line-through">
                          {formatPrice(p.original_price)}
                        </span>
                      )}
                      <span className="font-display text-2xl font-bold gold-text">{formatPrice(p.price)}</span>
                      <span className="text-xs text-emerald-100/60">una volta sola, 24 sorprese</span>
                    </p>
                    {hasPromo(p) && p.promo_label && (
                      <span data-testid="locked-modal-promo-label" className="inline-block rounded-full bg-[#8B1E2B] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-100">
                        {p.promo_label}
                      </span>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  data-testid="locked-modal-cta"
                  onClick={onPrimary}
                  className="w-full rounded-xl bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-600 px-6 py-3 font-bold text-[#1A1204] shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-amber-500/30"
                >
                  {live ? "Ho capito" : "Compra il calendario"}
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
